import { useCallback, useEffect, useState } from "react";
import type { Application } from "@applicant-reviewer/api";
import DevIndex from "./pages/DevIndex";
import ManagerOverview from "./apps/manager/pages/ManagerOverview";
import ManagerPipeline from "./apps/manager/pages/ManagerPipeline";
import ManagerInterviews from "./apps/manager/pages/ManagerInterviews";
import ManagerInterviewDetail from "./apps/manager/pages/ManagerInterviewDetail";
import ReviewRoute from "./apps/manager/pages/ReviewRoute";
import JobLandingDemo from "./apps/candidate/pages/JobLandingDemo";
import CandidateInterviewView from "./apps/candidate/pages/CandidateInterviewView";
import CandidateBenchView from "./apps/candidate/pages/CandidateBenchView";
import CandidateRejectView from "./apps/candidate/pages/CandidateRejectView";
import RecruiterApp from "./apps/recruiter/RecruiterApp";
import MainLayout from "./components/layout/MainLayout";
import ForecastView from "./pages/ForecastView";
import CampaignListView from "./pages/campaign/CampaignListView";
import CampaignDetailView from "./pages/campaign/CampaignDetailView";
import MyApplications from "./pages/MyApplications";
import CandidateApplicationDetail from "./pages/CandidateApplicationDetail";
import CandidateProfilePage from "./apps/candidate/pages/CandidateProfilePage";
import JobBrowsePage from "./pages/JobBrowsePage";
import { TourProvider } from "./context/TourContext";
import TourOverlay from "./components/tour/TourOverlay";

import ManagerLogin from "./pages/ManagerLogin";
import CandidateLoginPage from "./pages/CandidateLogin";
import { useAuth } from "./context/AuthContext";
import ZoomableScheduleCanvas from "./components/availability/ZoomableScheduleCanvas";
import type { WeeklyAvailability } from "@applicant-reviewer/api/availability";
import { useManagerAvailability } from "./hooks/queries/useManagerAvailability";
import { useSaveManagerAvailability } from "./hooks/mutations/useManagerAvailabilityMutations";
import { apiToCanvas, canvasToApi, emptyWeeklyAvailability } from "./lib/availability-converters";

type Route = "index" | "manager" | "manager-login" | "candidate-login" | "review" | "job" | "manager-schedule" | "pipeline" | "interviews" | "interview-detail" | "invite" | "bench" | "reject" | "recruiter" | "forecast" | "apply" | "campaigns" | "campaign-detail" | "my-applications" | "application-detail" | "profile" | "jobs-browse" | "not-found";

const getRouteFromLocation = (): { route: Route; params?: { id?: string; interviewId?: string; applicationId?: string } } => {
  const path = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);

  if (path === "/recruit/campaigns") return { route: "campaigns" };
  if (path === "/") return { route: "index" };
  if (path.startsWith("/recruit/campaigns/")) {
    const id = path.split("/recruit/campaigns/")[1];
    return { route: "campaign-detail", params: { id } };
  }
  if (path === "/recruit/forecast") return { route: "forecast" };
  // /apply/:jobId  OR  /apply?jobId=xxx
  if (path.startsWith("/apply")) return { route: "apply" };
  if (path === "/login/manager") return { route: "manager-login" };
  if (path === "/login/candidate") return { route: "candidate-login" };
  if (path === "/manager") return { route: "manager" };
  if (path === "/review") return { route: "review" };
  if (path === "/job") return { route: "job" };
  if (path === "/pipeline") return { route: "pipeline" };
  if (path === "/interviews") return { route: "interviews" };
  if (path.startsWith("/interview/")) {
    const id = path.split("/interview/")[1];
    return { route: "interview-detail", params: { id } };
  }
  if (path === "/my-applications") return { route: "my-applications" };
  if (path.startsWith("/my-applications/")) {
    const id = path.split("/my-applications/")[1];
    return { route: "application-detail", params: { id } };
  }
  if (path === "/profile") return { route: "profile" };
  if (path === "/jobs") return { route: "jobs-browse" };
  // /invite?interviewId=xxx  OR  /invite/:interviewId
  if (path.startsWith("/invite")) {
    const interviewId = searchParams.get("interviewId") ?? path.split("/invite/")[1] ?? "";
    return { route: "invite", params: { interviewId } };
  }
  // /bench?applicationId=xxx
  if (path.startsWith("/bench")) {
    const applicationId = searchParams.get("applicationId") ?? "";
    return { route: "bench", params: { applicationId } };
  }
  // /reject?applicationId=xxx
  if (path.startsWith("/reject")) {
    const applicationId = searchParams.get("applicationId") ?? "";
    return { route: "reject", params: { applicationId } };
  }
  if (path.startsWith("/recruiter")) return { route: "recruiter" };
  if (path === "/schedule") return { route: "manager-schedule" };
  return { route: "not-found" };
};

function App() {
  const [routeState, setRouteState] = useState(() => getRouteFromLocation());
  const route = routeState.route;
  const routeParams = routeState.params;

  const [initialApplicantId, setInitialApplicantId] = useState<string | undefined>(undefined);

  // Auth — must be before manager-only hooks so persona is available
  const { persona } = useAuth();

  // Manager Schedule State
  const managerAvailabilityQuery = useManagerAvailability({ enabled: persona === "manager" });
  const saveManagerAvailability = useSaveManagerAvailability();
  const [managerAvailability, setManagerAvailability] = useState<WeeklyAvailability>(() =>
    emptyWeeklyAvailability()
  );

  // Sync canvas state from API when data loads
  useEffect(() => {
    if (managerAvailabilityQuery.data?.availability) {
      setManagerAvailability(apiToCanvas(managerAvailabilityQuery.data.availability));
    }
  }, [managerAvailabilityQuery.data]);

  useEffect(() => {
    const handlePopState = () => {
      setRouteState(getRouteFromLocation());
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = useCallback((nextRoute: Route, params?: { id?: string; interviewId?: string; applicationId?: string }) => {
    setRouteState({ route: nextRoute, params });
    let path = "/";
    if (nextRoute === "index") path = "/";
    if (nextRoute === "campaigns") path = "/recruit/campaigns";
    if (nextRoute === "campaign-detail" && params?.id) path = `/recruit/campaigns/${params.id}`;
    if (nextRoute === "forecast") path = "/recruit/forecast";
    if (nextRoute === "apply") path = "/apply";
    if (nextRoute === "manager") path = "/manager";
    if (nextRoute === "review") path = "/review";
    if (nextRoute === "job") path = "/job";
    if (nextRoute === "manager-schedule") path = "/schedule";
    if (nextRoute === "pipeline") path = "/pipeline";
    if (nextRoute === "interviews") path = "/interviews";
    if (nextRoute === "interview-detail" && params?.id) path = `/interview/${params.id}`;
    if (nextRoute === "my-applications") path = "/my-applications";
    if (nextRoute === "application-detail" && params?.id) path = `/my-applications/${params.id}`;
    if (nextRoute === "profile") path = "/profile";
    if (nextRoute === "jobs-browse") path = "/jobs";
    if (nextRoute === "invite") {
      path = params?.interviewId ? `/invite?interviewId=${params.interviewId}` : "/invite";
    }
    if (nextRoute === "bench") {
      path = params?.applicationId ? `/bench?applicationId=${params.applicationId}` : "/bench";
    }
    if (nextRoute === "reject") {
      path = params?.applicationId ? `/reject?applicationId=${params.applicationId}` : "/reject";
    }
    if (nextRoute === "recruiter") path = "/recruiter";
    if (nextRoute === "manager-login") path = "/login/manager";
    if (nextRoute === "candidate-login") path = "/login/candidate";
    window.history.pushState({ route: nextRoute, params }, "", path);
  }, []);

  const handleRecruitNavigate = useCallback((path: string) => {
    if (path === "dashboard") navigate("manager");
    else if (path === "pipeline") navigate("pipeline");
    else if (path === "interviews") navigate("interviews");
    else if (path === "recruiter") navigate("recruiter");
    else if (path === "campaigns") navigate("campaigns");
    else if (path.startsWith("campaigns/")) {
      const id = path.split("campaigns/")[1];
      navigate("campaign-detail", { id });
    }
    else if (path === "forecast") navigate("forecast");
  }, [navigate]);

  const handleStartReview = useCallback(() => {
    setInitialApplicantId(undefined);
    navigate("review");
  }, [navigate]);

  const handleOpenApplicant = useCallback(
    (applicant: Application) => {
      setInitialApplicantId(applicant.applicationId);
      navigate("review");
    },
    [navigate]
  );

  const handleOpenInterview = useCallback(
    (candidateId: string) => {
      navigate("interview-detail", { id: candidateId });
    },
    [navigate]
  );

  const handleSetCalendar = useCallback(() => {
    navigate("manager-schedule");
  }, [navigate]);

  const handleNavigateToInterviews = useCallback(() => {
    navigate("interviews");
  }, [navigate]);

  // Auth guard — public routes don't require login
  const publicRoutes: Route[] = ["manager-login", "candidate-login", "apply", "job", "invite", "bench", "reject", "jobs-browse"];
  const managerRoutes: Route[] = ["manager", "review", "pipeline", "interviews", "recruiter", "campaigns", "campaign-detail", "forecast", "manager-schedule"];
  const candidateRoutes: Route[] = ["apply", "my-applications", "application-detail", "profile", "invite", "bench", "reject", "jobs-browse"];

  // Unauthenticated users can only see public routes
  if (persona === "unauthenticated" && !publicRoutes.includes(route)) {
    return <CandidateLoginPage />;
  }

  // Candidates on manager routes → redirect to candidate home
  if (persona === "candidate" && managerRoutes.includes(route)) {
    window.location.href = "/my-applications";
    return null;
  }

  // Managers on candidate-only routes → redirect to manager home
  if (persona === "manager" && candidateRoutes.includes(route)) {
    window.location.href = "/manager";
    return null;
  }

  if (route === "index") {
    if (persona === "candidate") {
      window.location.href = "/my-applications";
      return null;
    }
    if (persona === "manager") {
      window.location.href = "/manager";
      return null;
    }
    // Unauthenticated — show candidate login
    return <CandidateLoginPage />;
  }

  if (route === "manager-login") {
    return <ManagerLogin onSuccess={() => navigate("manager")} />;
  }

  if (route === "candidate-login") {
    return <CandidateLoginPage />;
  }

  if (route === "job") {
    return <JobLandingDemo />;
  }

  if (route === "invite") {
    return <CandidateInterviewView interviewId={routeParams?.interviewId ?? ""} />;
  }

  if (route === "bench") {
    return (
      <CandidateBenchView
        applicationId={routeParams?.applicationId ?? ""}
        onOptOut={() => navigate("reject", { applicationId: routeParams?.applicationId })}
      />
    );
  }

  if (route === "reject") {
    return <CandidateRejectView applicationId={routeParams?.applicationId} />;
  }

  if (route === "my-applications") {
    return (
      <MyApplications
        onViewDetail={(id) => navigate("application-detail", { id })}
      />
    );
  }

  if (route === "application-detail") {
    return (
      <CandidateApplicationDetail
        id={routeParams?.id ?? ""}
        onBack={() => navigate("my-applications")}
      />
    );
  }

  if (route === "profile") {
    return <CandidateProfilePage onBack={() => navigate("my-applications")} />;
  }

  if (route === "jobs-browse") {
    return (
      <JobBrowsePage
        onSelectJob={(jobId) => {
          navigate("apply");
          window.history.replaceState({}, "", `/apply?jobId=${jobId}`);
        }}
        onBack={persona === "candidate" ? () => navigate("my-applications") : undefined}
      />
    );
  }

  if (route === "apply") {
    return <JobLandingDemo />;
  }

  // All manager/recruiter routes use the sidebar layout
  if (["manager", "review", "pipeline", "interviews", "interview-detail", "recruiter", "campaigns", "campaign-detail", "forecast", "manager-schedule"].includes(route)) {
    const getCurrentPath = () => {
      if (route === "manager" || route === "review" || route === "manager-schedule") return "dashboard";
      if (route === "pipeline") return "pipeline";
      if (route === "interviews" || route === "interview-detail") return "interviews";
      if (route === "recruiter") return "recruiter";
      if (route === "campaigns" || route === "campaign-detail") return "campaigns";
      if (route === "forecast") return "forecast";
      return "dashboard";
    };

    return (
      <MainLayout currentPath={getCurrentPath()} onNavigate={handleRecruitNavigate}>
        {route === "manager" && (
          <div className="flex flex-col">
            <ManagerOverview
              mode="functional"
              onStartReview={handleStartReview}
              onNavigateToInterviews={handleNavigateToInterviews}
              onSetCalendar={handleSetCalendar}
            />
            <div className="border-t border-gray-200">
              <ManagerPipeline
                onNavigate={(route) => navigate(route as Route)}
                onOpenApplicant={handleOpenApplicant}
                onOpenInterview={handleOpenInterview}
              />
            </div>
          </div>
        )}
        {route === "review" && (
          <ReviewRoute
            initialApplicantId={initialApplicantId}
            onBack={() => navigate("manager")}
          />
        )}
        {route === "pipeline" && (
          <ManagerPipeline
            onNavigate={(route) => navigate(route as Route)}
            onOpenApplicant={handleOpenApplicant}
            onOpenInterview={handleOpenInterview}
          />
        )}
        {route === "interviews" && <ManagerInterviews />}
        {route === "interview-detail" && (
          <ManagerInterviewDetail interviewId={routeParams?.id} />
        )}
        {route === "recruiter" && <RecruiterApp />}
        {route === "manager-schedule" && (
          <ZoomableScheduleCanvas
            availability={managerAvailability}
            onChange={setManagerAvailability}
            onBack={() => navigate("manager")}
            onSave={() => {
              saveManagerAvailability.mutate(
                { availability: canvasToApi(managerAvailability) },
                { onSuccess: () => navigate("manager") }
              );
            }}
            isManagerMode={true}
          />
        )}
        {route === "campaigns" && <CampaignListView onNavigate={handleRecruitNavigate} />}
        {route === "campaign-detail" && routeParams?.id && <CampaignDetailView campaignId={routeParams.id} />}
        {route === "forecast" && <ForecastView />}
      </MainLayout>
    );
  }

  // Dev-only index page
  if (import.meta.env.DEV && route === "not-found") {
    return <DevIndex />;
  }

  // 404 for unrecognized routes in production
  return (
    <div className="flex flex-col items-center justify-center min-h-screen text-center p-8">
      <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
      <p className="text-gray-500 mb-6">Page not found.</p>
      <button
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        onClick={() => { window.location.href = "/"; }}
      >
        Go home
      </button>
    </div>
  );
}

function AppWithTour() {
  return (
    <TourProvider>
      <App />
      <TourOverlay />
    </TourProvider>
  );
}

export default AppWithTour;
